import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Users, Building2, Briefcase, CreditCard, Activity, Shield,
  CheckCircle, XCircle, Clock, Search, ChevronRight, ChevronLeft,
  Eye, Ban, UserCheck, UserX, Crown, Euro, TrendingUp,
  MessageSquare, FileText, Loader2, AlertTriangle, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Stat Card ───────────────────────────
const StatCard = ({ icon: Icon, label, value, sublabel, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {sublabel && <p className="text-xs text-muted-foreground/70">{sublabel}</p>}
        </div>
      </div>
    </div>
  );
};

// ─── Badge ──────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active: { label: 'Actif', cls: 'bg-green-100 text-green-700' },
    pending: { label: 'En attente', cls: 'bg-yellow-100 text-yellow-700' },
    approved: { label: 'Approuvé', cls: 'bg-green-100 text-green-700' },
    rejected: { label: 'Rejeté', cls: 'bg-red-100 text-red-700' },
    suspended: { label: 'Suspendu', cls: 'bg-orange-100 text-orange-700' },
    blocked: { label: 'Bloqué', cls: 'bg-red-100 text-red-700' },
    paid: { label: 'Payé', cls: 'bg-green-100 text-green-700' },
    initiated: { label: 'Initié', cls: 'bg-blue-100 text-blue-700' },
  };
  const info = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${info.cls}`}>{info.label}</span>;
};

// ════════════════════════════════════════
// MAIN ADMIN DASHBOARD
// ════════════════════════════════════════
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [validations, setValidations] = useState({ items: [], total: 0 });
  const [payments, setPayments] = useState({ items: [], total: 0, page: 1, pages: 1, totals: {} });
  const [activity, setActivity] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  // Filters
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [paymentPage, setPaymentPage] = useState(1);
  const [validationTypeFilter, setValidationTypeFilter] = useState('');

  const token = localStorage.getItem('sfretire_token');
  const user = (() => { try { return JSON.parse(localStorage.getItem('sfretire_user') || 'null'); } catch { return null; } })();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token || !user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchStats();
    fetchActivity();
  }, []);

  useEffect(() => { if (activeTab === 'users') fetchUsers(); }, [activeTab, userRoleFilter, userStatusFilter, userSearch, userPage]);
  useEffect(() => { if (activeTab === 'validations') fetchValidations(); }, [activeTab, validationTypeFilter]);
  useEffect(() => { if (activeTab === 'payments') fetchPayments(); }, [activeTab, paymentTypeFilter, paymentStatusFilter, paymentPage]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchActivity = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/activity?limit=15`, { headers });
      if (res.ok) { const d = await res.json(); setActivity(d.items || []); }
    } catch (e) { console.error(e); }
  };

  const fetchUsers = async () => {
    const params = new URLSearchParams({ page: userPage, limit: 15 });
    if (userRoleFilter) params.set('role', userRoleFilter);
    if (userStatusFilter) params.set('status', userStatusFilter);
    if (userSearch) params.set('search', userSearch);
    try {
      const res = await fetch(`${API_URL}/api/admin/users?${params}`, { headers });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchValidations = async () => {
    const params = validationTypeFilter ? `?profile_type=${validationTypeFilter}` : '';
    try {
      const res = await fetch(`${API_URL}/api/admin/validations${params}`, { headers });
      if (res.ok) setValidations(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchPayments = async () => {
    const params = new URLSearchParams({ page: paymentPage, limit: 15 });
    if (paymentTypeFilter) params.set('payment_type', paymentTypeFilter);
    if (paymentStatusFilter) params.set('status', paymentStatusFilter);
    try {
      const res = await fetch(`${API_URL}/api/admin/payments?${params}`, { headers });
      if (res.ok) setPayments(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleUserStatus = async (userId, newStatus) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/status?status=${newStatus}`, {
        method: 'PUT', headers
      });
      if (res.ok) {
        toast.success(`Utilisateur ${newStatus === 'active' ? 'activé' : newStatus === 'suspended' ? 'suspendu' : 'bloqué'}`);
        fetchUsers();
        fetchStats();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur réseau'); }
    setActionLoading(null);
  };

  const handleValidation = async (profileType, userId, action, reason = '') => {
    setActionLoading(`${profileType}-${userId}`);
    const params = new URLSearchParams({ action });
    if (reason) params.set('reason', reason);
    try {
      const res = await fetch(`${API_URL}/api/admin/validate/${profileType}/${userId}?${params}`, {
        method: 'PUT', headers
      });
      if (res.ok) {
        toast.success(`Profil ${action === 'approved' ? 'approuvé' : 'rejeté'}`);
        fetchValidations();
        fetchStats();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur réseau'); }
    setActionLoading(null);
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Activity },
    { id: 'users', label: 'Utilisateurs', icon: Users, badge: stats?.users?.total },
    { id: 'validations', label: 'Validations', icon: Shield, badge: stats?.profiles_pending?.total },
    { id: 'payments', label: 'Paiements', icon: CreditCard },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
      {/* Top Bar */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Shield size={20} className="text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Administration sfretire.com</h1>
              <p className="text-xs text-muted-foreground">Gestion de la plateforme</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="admin-back-btn">
            Retour au site
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-card border border-border rounded-xl p-1 mb-6" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
              {tab.badge > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary/10 text-primary'
                }`}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <StatCard icon={Users} label="Utilisateurs" value={stats.users.total} sublabel={`+${stats.users.new_30d} ce mois`} color="blue" />
              <StatCard icon={Building2} label="Entreprises" value={stats.users.entreprises} color="purple" />
              <StatCard icon={Users} label="Retraités" value={stats.users.retraites} color="green" />
              <StatCard icon={Briefcase} label="Missions" value={stats.missions.total} sublabel={`${stats.missions.published} publiées`} color="amber" />
              <StatCard icon={Euro} label="Revenus" value={`${stats.revenue.total.toLocaleString('fr-FR')}EUR`} color="green" />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              <StatCard icon={AlertTriangle} label="Validations en attente" value={stats.profiles_pending.total} sublabel={`${stats.profiles_pending.retraites} retraités, ${stats.profiles_pending.entreprises} entreprises`} color="amber" />
              <StatCard icon={FileText} label="Candidatures" value={stats.applications.total} sublabel={`${stats.applications.pending} en attente`} color="blue" />
              <StatCard icon={MessageSquare} label="Conversations" value={stats.conversations} color="purple" />
            </div>

            {/* Recent Activity */}
            <div className="bg-card border border-border rounded-xl">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="font-semibold text-foreground">Activité récente</h2>
              </div>
              <div className="divide-y divide-border max-h-96 overflow-y-auto">
                {activity.map((item, i) => {
                  const iconMap = {
                    user_registered: <Users size={14} className="text-blue-500" />,
                    mission_created: <Briefcase size={14} className="text-amber-500" />,
                    application_submitted: <FileText size={14} className="text-green-500" />,
                    payment: <CreditCard size={14} className="text-purple-500" />,
                  };
                  return (
                    <div key={i} className="px-6 py-3 flex items-center gap-3 text-sm">
                      <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        {iconMap[item.type] || <Activity size={14} />}
                      </div>
                      <span className="text-foreground flex-1">{item.description}</span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {item.timestamp ? new Date(item.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  );
                })}
                {activity.length === 0 && (
                  <div className="px-6 py-8 text-center text-muted-foreground">Aucune activité récente</div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* ── USERS TAB ── */}
        {activeTab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {/* Filters */}
            <div className="bg-card border border-border rounded-xl p-4 flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Rechercher par email..."
                  value={userSearch}
                  onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="users-search-input"
                />
              </div>
              <select
                value={userRoleFilter}
                onChange={e => { setUserRoleFilter(e.target.value); setUserPage(1); }}
                className="h-9 px-3 rounded-lg border border-input bg-background text-sm"
                data-testid="users-role-filter"
              >
                <option value="">Tous les rôles</option>
                <option value="retraite">Retraités</option>
                <option value="entreprise">Entreprises</option>
                <option value="admin">Admins</option>
              </select>
              <select
                value={userStatusFilter}
                onChange={e => { setUserStatusFilter(e.target.value); setUserPage(1); }}
                className="h-9 px-3 rounded-lg border border-input bg-background text-sm"
                data-testid="users-status-filter"
              >
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="suspended">Suspendu</option>
                <option value="blocked">Bloqué</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="users-table">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-secondary/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b">
                <div className="col-span-4">Utilisateur</div>
                <div className="col-span-2">Rôle</div>
                <div className="col-span-2">Statut</div>
                <div className="col-span-2">Inscription</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              <div className="divide-y divide-border">
                {users.items.map(u => {
                  const profileName = u.role === 'retraite'
                    ? `${u.profile?.first_name || ''} ${u.profile?.last_name || ''}`.trim()
                    : u.profile?.company_name || '';
                  return (
                    <div key={u.id || u.email} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 items-center hover:bg-secondary/30 transition-colors" data-testid={`user-row-${u.email}`}>
                      <div className="col-span-4">
                        <p className="text-sm font-medium text-foreground">{u.email}</p>
                        {profileName && <p className="text-xs text-muted-foreground">{profileName}</p>}
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                          u.role === 'entreprise' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {u.role === 'admin' ? <Shield size={12}/> : u.role === 'entreprise' ? <Building2 size={12}/> : <Users size={12}/>}
                          {u.role}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <StatusBadge status={u.status || 'active'} />
                      </div>
                      <div className="col-span-2 text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                      </div>
                      <div className="col-span-2 flex gap-1 justify-end">
                        {u.role !== 'admin' && (
                          <>
                            {u.status !== 'suspended' && (
                              <Button variant="ghost" size="sm" className="text-orange-600 h-8 px-2" disabled={actionLoading === (u.id || u.email)} onClick={() => handleUserStatus(u.id, 'suspended')} data-testid={`suspend-btn-${u.email}`}>
                                <Ban size={14} />
                              </Button>
                            )}
                            {u.status === 'suspended' && (
                              <Button variant="ghost" size="sm" className="text-green-600 h-8 px-2" disabled={actionLoading === (u.id || u.email)} onClick={() => handleUserStatus(u.id, 'active')} data-testid={`activate-btn-${u.email}`}>
                                <UserCheck size={14} />
                              </Button>
                            )}
                            {u.status !== 'blocked' && (
                              <Button variant="ghost" size="sm" className="text-red-600 h-8 px-2" disabled={actionLoading === (u.id || u.email)} onClick={() => handleUserStatus(u.id, 'blocked')} data-testid={`block-btn-${u.email}`}>
                                <XCircle size={14} />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {users.items.length === 0 && (
                  <div className="px-6 py-12 text-center text-muted-foreground">Aucun utilisateur trouvé</div>
                )}
              </div>
              {/* Pagination */}
              {users.pages > 1 && (
                <div className="px-6 py-3 border-t border-border flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{users.total} utilisateur{users.total > 1 ? 's' : ''}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={userPage <= 1} onClick={() => setUserPage(p => p - 1)}>
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="px-3 py-1 text-sm">{userPage}/{users.pages}</span>
                    <Button variant="outline" size="sm" disabled={userPage >= users.pages} onClick={() => setUserPage(p => p + 1)}>
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ── VALIDATIONS TAB ── */}
        {activeTab === 'validations' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-4 flex gap-3 items-center">
              <Filter size={16} className="text-muted-foreground" />
              <select
                value={validationTypeFilter}
                onChange={e => setValidationTypeFilter(e.target.value)}
                className="h-9 px-3 rounded-lg border border-input bg-background text-sm"
                data-testid="validation-type-filter"
              >
                <option value="">Tous les profils</option>
                <option value="retraite">Retraités</option>
                <option value="entreprise">Entreprises</option>
              </select>
              <span className="text-sm text-muted-foreground ml-auto">
                {validations.total} profil{validations.total !== 1 ? 's' : ''} en attente
              </span>
            </div>

            {validations.items.length === 0 ? (
              <div className="bg-card border border-border rounded-xl py-16 text-center">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
                <p className="text-lg text-muted-foreground">Aucun profil en attente de validation</p>
                <p className="text-sm text-muted-foreground mt-1">Tous les profils ont été traités</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="validations-list">
                {validations.items.map((item, i) => {
                  const p = item.profile || {};
                  const u = item.user || {};
                  const isRetraite = item.type === 'retraite';
                  const name = isRetraite
                    ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
                    : p.company_name || '';
                  const loadingKey = `${item.type}-${p.user_id}`;

                  return (
                    <div key={i} className="bg-card border border-border rounded-xl p-6" data-testid={`validation-card-${p.user_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isRetraite ? 'bg-green-100' : 'bg-blue-100'}`}>
                            {isRetraite ? <Users size={24} className="text-green-600" /> : <Building2 size={24} className="text-blue-600" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground">{name || 'Sans nom'}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${isRetraite ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                {isRetraite ? 'Retraité' : 'Entreprise'}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            {isRetraite && (
                              <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                {p.title && <span className="bg-secondary px-2 py-1 rounded">{p.title}</span>}
                                {p.experience_years && <span className="bg-secondary px-2 py-1 rounded">{p.experience_years} ans exp.</span>}
                                {p.skills?.slice(0, 3).map((s, si) => <span key={si} className="bg-secondary px-2 py-1 rounded">{s}</span>)}
                              </div>
                            )}
                            {!isRetraite && (
                              <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                {p.sector && <span className="bg-secondary px-2 py-1 rounded">{p.sector}</span>}
                                {p.size && <span className="bg-secondary px-2 py-1 rounded">{p.size} employés</span>}
                                {p.contact_name && <span className="bg-secondary px-2 py-1 rounded">Contact: {p.contact_name}</span>}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Inscrit le {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            disabled={actionLoading === loadingKey}
                            onClick={() => {
                              const reason = window.prompt('Motif du rejet (optionnel) :');
                              if (reason !== null) handleValidation(item.type, p.user_id, 'rejected', reason);
                            }}
                            data-testid={`reject-btn-${p.user_id}`}
                          >
                            {actionLoading === loadingKey ? <Loader2 size={14} className="animate-spin" /> : <><UserX size={14} className="mr-1" /> Rejeter</>}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            disabled={actionLoading === loadingKey}
                            onClick={() => handleValidation(item.type, p.user_id, 'approved')}
                            data-testid={`approve-btn-${p.user_id}`}
                          >
                            {actionLoading === loadingKey ? <Loader2 size={14} className="animate-spin" /> : <><UserCheck size={14} className="mr-1" /> Approuver</>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ── PAYMENTS TAB ── */}
        {activeTab === 'payments' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {/* Revenue Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard icon={Euro} label="Total facturé" value={`${(payments.totals?.total_amount || 0).toLocaleString('fr-FR')}EUR`} color="blue" />
              <StatCard icon={TrendingUp} label="Revenus encaissés" value={`${(payments.totals?.paid_amount || 0).toLocaleString('fr-FR')}EUR`} color="green" />
              <StatCard icon={CreditCard} label="Transactions" value={payments.total} color="purple" />
            </div>

            {/* Filters */}
            <div className="bg-card border border-border rounded-xl p-4 flex gap-3 items-center">
              <Filter size={16} className="text-muted-foreground" />
              <select value={paymentTypeFilter} onChange={e => { setPaymentTypeFilter(e.target.value); setPaymentPage(1); }}
                className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="payment-type-filter">
                <option value="">Tous les types</option>
                <option value="subscription">Abonnements</option>
                <option value="commission">Commissions</option>
              </select>
              <select value={paymentStatusFilter} onChange={e => { setPaymentStatusFilter(e.target.value); setPaymentPage(1); }}
                className="h-9 px-3 rounded-lg border border-input bg-background text-sm" data-testid="payment-status-filter">
                <option value="">Tous les statuts</option>
                <option value="paid">Payé</option>
                <option value="pending">En attente</option>
              </select>
            </div>

            {/* Payments Table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="payments-table">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-secondary/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b">
                <div className="col-span-2">Date</div>
                <div className="col-span-3">Entreprise</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Montant</div>
                <div className="col-span-2">Statut</div>
                <div className="col-span-1">Plan</div>
              </div>
              <div className="divide-y divide-border">
                {payments.items.map(tx => (
                  <div key={tx.session_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 items-center hover:bg-secondary/30 transition-colors" data-testid={`payment-row-${tx.session_id}`}>
                    <div className="col-span-2 text-sm">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm font-medium text-foreground">{tx.company_name || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">{tx.user_email || ''}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        tx.type === 'subscription' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {tx.type === 'subscription' ? <Crown size={12} /> : <Briefcase size={12} />}
                        {tx.type === 'subscription' ? 'Abo' : 'Commission'}
                      </span>
                    </div>
                    <div className="col-span-2 text-sm font-semibold">{tx.amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}&nbsp;&euro;</div>
                    <div className="col-span-2"><StatusBadge status={tx.payment_status || tx.status} /></div>
                    <div className="col-span-1 text-xs text-muted-foreground capitalize">{(tx.plan_id || '').replace('_', ' ')}</div>
                  </div>
                ))}
                {payments.items.length === 0 && (
                  <div className="px-6 py-12 text-center text-muted-foreground">Aucune transaction</div>
                )}
              </div>
              {payments.pages > 1 && (
                <div className="px-6 py-3 border-t border-border flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{payments.total} transaction{payments.total > 1 ? 's' : ''}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={paymentPage <= 1} onClick={() => setPaymentPage(p => p - 1)}>
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="px-3 py-1 text-sm">{paymentPage}/{payments.pages}</span>
                    <Button variant="outline" size="sm" disabled={paymentPage >= payments.pages} onClick={() => setPaymentPage(p => p + 1)}>
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
